import { DOCUMENT, Location } from "@angular/common";
import { inject, Injectable, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";

import {
    Context,
    getBuildVersion,
    LOCALE,
    Settings,
    StorageManager,
    StorageService,
} from "@hermes/app-core";
import { UserFacade } from "@hermes/states/user";
import {
    AnalyticsService,
    getAnalyticsDeviceType,
    GTMClientGender,
    GTMClientType,
} from "@hermes/fragments/analytics";

import { UrlUtils } from "@hermes/utils-generic/services/user-interface";

import { PageViewGridPage } from "../../events/page-view-grid-page.event";

@Injectable()
export class GridAnalyticsService implements OnDestroy {
    private context = inject(Context);
    private userFacade = inject(UserFacade);
    private analyticsService = inject(AnalyticsService);
    private storageService = inject(StorageService);
    private location = inject(Location);
    private settings = inject(Settings);
    private urlUtils = inject(UrlUtils);
    private document = inject(DOCUMENT);
    private locale = inject(LOCALE);

    private subscription = new Subscription();
    private sessionStorage!: StorageManager;

    constructor() {
        this.sessionStorage =
            this.storageService.getSessionStorageInstance() as StorageManager;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Send analytics data for grid page only in angular first
     */
    public sendGridPageViewEvent(pagetype: string): void {
        if (!this.context.isInBrowserMode()) {
            return;
        }

        this.subscription.add(
            this.userFacade.userIsSynchronized$
                .pipe(
                    filter((isSynchro) => isSynchro),
                    switchMap(() => this.userFacade.userDetails$),
                    take(1),
                )
                .subscribe((user) => {
                    this.analyticsService.sendData(
                        new PageViewGridPage({
                            pagename: this.document.title,
                            buildversion: getBuildVersion(this.settings.envKey),
                            devicetype: getAnalyticsDeviceType(),
                            languagecode: this.locale.langCode,
                            countrycode: this.locale.countryCode,
                            userId: undefined,
                            clienttype:
                                (user.userType as GTMClientType) ||
                                "non-connected",
                            clientgender: user.prefix as GTMClientGender,
                            pageTranslate: this.location
                                .path()
                                .replace(/^\/+/, ""), // url after baseHref/locale, and without first slash.
                            pagetype,
                            menuLvl: this.sessionStorage
                                ?.getItem<string>("nav")
                                ?.split("/"),
                            previousPage: this.urlUtils.getPreviousUrl(),
                        }),
                    );
                }),
        );
    }
}
